<template>
  <div data-component-group="filter-forms" data-component="network">
    <FiltersForm
      has-phone-sheet
      :applied-filters="appliedFilters"
      :on-clear-filter="clearFilter"
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="countries"
    >
      <template v-slot:phone-actions>
        <slot name="phone-actions" />
      </template>

      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        :return-object="true"
        item-title="name"
        item-value="id"
        :label="$t('business_name')"
        v-model:search="businessSearch"
      />

      <v-autocomplete
        clearable
        v-model="network"
        :items="networks"
        :loading="isLoadingNetworks"
        prepend-inner-icon="share"
        :return-object="true"
        item-title="name"
        :label="$t('network_name')"
        v-model:search="networkSearch"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        :return-object="true"
        item-title="name"
        :label="$t('status')"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import FiltersForm from './FiltersBase';
  import filterUrlSync from '@/mixins/filterUrlSync';
  import filterChips, { platformChips } from '@/mixins/filterChips';
  import { isNonEmptyArr, isNonEmptyStr, isNum, isStr, debounce } from '../../../utils';

  const searchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    mixins: [filterUrlSync, filterChips],
    components: {
      FiltersForm,
    },
    emits: ['filter', 'clearFilters'],
    data() {
      return {
        urlConfig: [
          { field: 'countries', urlKey: 'countries', type: 'string[]' },
          {
            // Looked up by id: the business the URL names can sit outside the
            // page getBusinesses() returns, and it has to end up in the options.
            field: 'business', urlKey: 'business', type: 'number', prop: 'id',
            fetchByIds: 'getBusinessesByIds',
          },
          {
            // The filter sends this one as a search string, so the name the URL
            // carries is the whole value. item-title is `name` too, so the stub
            // renders correctly with no fetch.
            field: 'network', urlKey: 'network', type: 'string', prop: 'name',
          },
          {
            field: 'status', urlKey: 'status', type: 'number', prop: 'id',
            fetchMethod: 'getStatuses', itemsField: 'statuses',
          },
        ],
        business: null,
        countries: [],
        network: null,
        status: null,
        networks: [],
        networkSearch: '',
        businessSearch: '',
        isLoadingNetworks: false,
        isLoadingBusinesses: false,
        isRefreshingNetworks: false,
        isRefreshingBusinesses: false,
      };
    },
    computed: {
      filterChipFields() {
        const platforms = this.$store.getters['users/countries'] || [];

        return [
          {
            model: 'countries',
            label: this.$t('platform'),
            toChips: platformChips(platforms),
          },
          { model: 'business', label: this.$t('business_name') },
          { model: 'network', label: this.$t('network_name') },
          { model: 'status', label: this.$t('status') },
        ];
      },
      businesses() {
        const items = this.$store.getters['businesses/items'];
        // A business restored from the URL is looked up by id, so it is not in
        // the listing the store holds. Keep it among the options.
        return this.business && !items.some(({ id }) => id === this.business.id)
          ? [...items, this.business]
          : items;
      },
      statuses() {
        return this.$store.getters['networks/statuses'];
      },
      hasFilters() {
        const { business = {}, countries = [], network = {}, status = {} } = this;

        return (
          isNum(business?.id) ||
          isNonEmptyArr(countries) ||
          // A network restored from the URL is a { name } stub with no id
          // (see the urlConfig comment above) — isNum(network?.id) alone
          // missed that case and reported no active filter.
          isNonEmptyStr(network?.name) ||
          isNum(status?.id)
        );
      },
    },
    watch: {
      business() {
        this.isRefreshingBusinesses = true;
      },
      network() {
        this.isRefreshingNetworks = true;
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusinesses) {
            this.isRefreshingBusinesses = false;
            return false;
          }

          searchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      networkSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingNetworks) {
            this.isRefreshingNetworks = false;
            return false;
          }

          searchDebouncer(() => this.searchNetworks(nextSearch));
        }
      },
    },
    methods: {
      getStatuses() {
        return this.$store.dispatch('networks/getStatuses');
      },
      getBusinessesByIds(ids) {
        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItemsByIds', { ids })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;

        return this.$store
          .dispatch('businesses/getItems', query)
          .then((res) => {
            this.isLoadingBusinesses = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      searchNetworks(search = '') {
        const query = {
          per_page: 15,
          search,
        };

        this.isLoadingNetworks = true;

        return this.$store
          .dispatch('networks/searchItems', query)
          .then((res) => {
            this.networks = res.data.data;
            this.isLoadingNetworks = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingNetworks = false;
            return err;
          });
      },
      onFilter() {
        const { business = {}, countries = [], network = {}, status = {} } = this;

        this.syncFiltersToUrl();
        this.rememberAppliedFilters();

        this.$emit('filter', {
          business_id: business?.id,
          countries,
          search: network?.name,
          status: status?.id,
        });
      },
      onClearFilters() {
        this.countries = [];
        this.networks = [];

        this.business = null;
        this.network = null;
        this.status = null;

        this.clearFiltersUrl();
        this.forgetAppliedFilters();

        this.$emit('clearFilters');
      },
    },
  };
</script>
